import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { colors, spacing, borderRadius, typography, shadows } from '@serps/ui-kit/src/tokens';
import { Button } from '@serps/ui-kit/src/components/Button';
import { Spinner } from '@serps/ui-kit/src/components/Spinner';
import { apiGet, apiPost, type AuthUser } from '../lib/auth';
import { useLang } from '../lib/lang';
import { Toast, type ToastVariant } from '../components/Toast';

interface Props { user: AuthUser }

interface MoraSchool {
  id: string; school_id: string; school_name: string; school_code: string;
  district: string; phone: string | null;
  mora_code: string; mora_level: string;
  registered_ustaz: number; registered_ustazah: number; student_count: number;
  operating_days: string[]; operating_hours: string | null;
  curriculum_code: string | null; last_inspection_date: string | null;
  inspection_status: string | null; notes: string | null; updated_at: string;
}

interface MoraStats {
  overview: { total_schools: string; total_ustaz: string; total_ustazah: string; total_students: string };
  byLevel:  Array<{ mora_level: string; count: string; students: string }>;
  byDistrict: Array<{ district: string; mora_school_count: string; students: string }>;
}

const MORA_LEVELS = ['TADIKA', 'UGAMA', 'MENENGAH_UGAMA', 'TAHFIZ'] as const;
const DAYS_OF_WEEK = ['Ahad','Isnin','Selasa','Rabu','Khamis','Jumaat','Sabtu'];
const DISTRICTS = ['brunei-muara', 'tutong', 'belait', 'temburong'];
const DIST_LABELS: Record<string, string> = { 'brunei-muara': 'Brunei Muara', tutong: 'Tutong', belait: 'Belait', temburong: 'Temburong' };
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function MoraSchoolsPage({ user }: Props) {
  const { lang } = useLang();
  const E = lang === 'en';
  const [tab,        setTab]        = useState<'schools' | 'stats'>('schools');
  const [schools,    setSchools]    = useState<MoraSchool[]>([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [toast,      setToast]      = useState<{ message: string; variant: ToastVariant } | null>(null);

  const [stats,      setStats]      = useState<MoraStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError,   setStatsError]   = useState<string | null>(null);

  const [filterLevel,    setFilterLevel]    = useState('');
  const [filterDistrict, setFilterDistrict] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    schoolId: '', moraCode: '', moraLevel: 'UGAMA',
    registeredUstaz: '0', registeredUstazah: '0', studentCount: '0',
    operatingDays: [] as string[], operatingHours: '',
    curriculumCode: '', lastInspectionDate: '', inspectionStatus: '', notes: '',
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [saving,    setSaving]    = useState(false);

  const isAdmin = user.roles.some((r) => ['admin', 'super_admin'].includes(r.role));

  const load = useCallback(async (p: number) => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ page: String(p) });
      if (filterLevel)    params.set('moraLevel', filterLevel);
      if (filterDistrict) params.set('district',  filterDistrict);
      const result = await apiGet<{ data: MoraSchool[]; total: number }>(`/api/mora/schools?${params}`);
      setSchools(result.data); setTotal(result.total); setPage(p);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ralat memuatkan sekolah MORA. / Failed to load MORA schools.');
    } finally { setLoading(false); }
  }, [filterLevel, filterDistrict]);

  const loadStats = useCallback(async () => {
    setStatsLoading(true); setStatsError(null);
    try { setStats(await apiGet<MoraStats>('/api/mora/stats')); }
    catch (err) { setStatsError(err instanceof Error ? err.message : 'Ralat memuatkan statistik.'); }
    finally { setStatsLoading(false); }
  }, []);

  useEffect(() => { if (tab === 'schools') void load(1); }, [tab, load]);
  useEffect(() => { if (tab === 'stats')   void loadStats(); }, [tab, loadStats]);

  function validateForm(): string | null {
    if (!form.schoolId.trim())  return 'ID sekolah diperlukan. / School ID required.';
    if (!UUID_RE.test(form.schoolId.trim())) return 'ID sekolah tidak sah (mesti UUID). / Invalid school ID (must be UUID).';
    if (!form.moraCode.trim())  return 'Kod MORA diperlukan. / MORA code required.';
    if (form.moraCode.trim().length > 30) return 'Kod MORA terlalu panjang (maks 30). / MORA code too long (max 30).';
    if (!form.moraLevel)        return 'Peringkat MORA diperlukan. / MORA level required.';
    const uz  = parseInt(form.registeredUstaz);
    const uzh = parseInt(form.registeredUstazah);
    const sc  = parseInt(form.studentCount);
    if (isNaN(uz)  || uz  < 0 || uz  > 999) return 'Bilangan ustaz mesti 0–999. / Ustaz count must be 0–999.';
    if (isNaN(uzh) || uzh < 0 || uzh > 999) return 'Bilangan ustazah mesti 0–999. / Ustazah count must be 0–999.';
    if (isNaN(sc)  || sc  < 0 || sc  > 9999) return 'Bilangan pelajar mesti 0–9999. / Student count must be 0–9999.';
    if (form.lastInspectionDate && form.lastInspectionDate > new Date().toISOString().slice(0, 10))
      return 'Tarikh pemeriksaan tidak boleh masa hadapan. / Inspection date cannot be in the future.';
    return null;
  }

  async function handleSave() {
    const err = validateForm();
    if (err) { setFormError(err); return; }
    setSaving(true); setFormError(null);
    try {
      await apiPost('/api/mora/schools', {
        schoolId:           form.schoolId.trim(),
        moraCode:           form.moraCode.trim(),
        moraLevel:          form.moraLevel,
        registeredUstaz:    parseInt(form.registeredUstaz) || 0,
        registeredUstazah:  parseInt(form.registeredUstazah) || 0,
        studentCount:       parseInt(form.studentCount) || 0,
        operatingDays:      form.operatingDays,
        operatingHours:     form.operatingHours.trim() || undefined,
        curriculumCode:     form.curriculumCode.trim() || undefined,
        lastInspectionDate: form.lastInspectionDate || undefined,
        inspectionStatus:   form.inspectionStatus.trim() || undefined,
        notes:              form.notes.trim() || undefined,
      });
      setToast({ variant: 'success', message: 'Rekod MORA berjaya disimpan. / MORA record saved.' });
      setShowForm(false);
      setForm({ schoolId: '', moraCode: '', moraLevel: 'UGAMA', registeredUstaz: '0', registeredUstazah: '0', studentCount: '0', operatingDays: [], operatingHours: '', curriculumCode: '', lastInspectionDate: '', inspectionStatus: '', notes: '' });
      void load(1);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Gagal menyimpan. / Failed to save.');
    } finally { setSaving(false); }
  }

  function toggleDay(day: string) {
    setForm((f) => ({
      ...f,
      operatingDays: f.operatingDays.includes(day)
        ? f.operatingDays.filter((d) => d !== day)
        : [...f.operatingDays, day],
    }));
  }

  const totalPages = Math.ceil(total / 25) || 1;

  return (
    <>
      <div style={{ minHeight: '100vh', backgroundColor: colors.neutral.black, fontFamily: typography.fontFamily.body, padding: `${spacing.xl} ${spacing.md}` }}>
        <div style={{ maxWidth: '960px', margin: '0 auto' }}>
          <div style={{ marginBottom: spacing.xl }}>
            <Link to="/" style={{ color: colors.neutral.gray400, fontSize: typography.fontSize.sm, textDecoration: 'none' }}>← Laman Utama</Link>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: spacing.xl, gap: spacing.md, flexWrap: 'wrap' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: colors.neutral.white }}>
                🕌 MORA — Sekolah Ugama/Jawi
              </h1>
              <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>
                Ministry of Religious Affairs — {total} sekolah berdaftar
              </p>
            </div>
            {isAdmin && tab === 'schools' && !showForm && (
              <Button variant="primary" size="sm" onClick={() => { setShowForm(true); setFormError(null); }}>
                + Daftar / Kemaskini Sekolah
              </Button>
            )}
          </div>

          {/* Upsert form */}
          {showForm && (
            <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, marginBottom: spacing.lg }}>
              <p style={{ margin: `0 0 ${spacing.lg}`, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>
                Daftar / Kemaskini Sekolah MORA / Register or Update MORA School
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
                <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                  <LF label="ID Sekolah (UUID) *" style={{ flex: '2 1 200px' }}>
                    <input type="text" value={form.schoolId} onChange={(e) => setForm((f) => ({ ...f, schoolId: e.target.value }))}
                      placeholder="UUID sekolah" disabled={saving} style={inputSt()} />
                  </LF>
                  <LF label="Kod MORA *" style={{ flex: '1 1 140px' }}>
                    <input type="text" value={form.moraCode} onChange={(e) => setForm((f) => ({ ...f, moraCode: e.target.value.toUpperCase() }))}
                      placeholder="cth: MORA-001" disabled={saving} style={inputSt()} maxLength={30} />
                  </LF>
                  <LF label="Peringkat MORA *" style={{ flex: '1 1 160px' }}>
                    <select value={form.moraLevel} onChange={(e) => setForm((f) => ({ ...f, moraLevel: e.target.value }))}
                      disabled={saving} style={inputSt()}>
                      {MORA_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </LF>
                </div>
                <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                  <LF label="Bilangan Ustaz" style={{ flex: '1 1 120px' }}>
                    <input type="number" value={form.registeredUstaz} onChange={(e) => setForm((f) => ({ ...f, registeredUstaz: e.target.value }))}
                      disabled={saving} min={0} max={999} style={inputSt()} />
                  </LF>
                  <LF label="Bilangan Ustazah" style={{ flex: '1 1 120px' }}>
                    <input type="number" value={form.registeredUstazah} onChange={(e) => setForm((f) => ({ ...f, registeredUstazah: e.target.value }))}
                      disabled={saving} min={0} max={999} style={inputSt()} />
                  </LF>
                  <LF label="Bilangan Pelajar" style={{ flex: '1 1 120px' }}>
                    <input type="number" value={form.studentCount} onChange={(e) => setForm((f) => ({ ...f, studentCount: e.target.value }))}
                      disabled={saving} min={0} max={9999} style={inputSt()} />
                  </LF>
                  <LF label="Waktu Operasi" style={{ flex: '1 1 160px' }}>
                    <input type="text" value={form.operatingHours} onChange={(e) => setForm((f) => ({ ...f, operatingHours: e.target.value }))}
                      placeholder="cth: 2:00pm–5:00pm" disabled={saving} style={inputSt()} maxLength={50} />
                  </LF>
                </div>
                <div>
                  <label style={labelSt}>Hari Operasi / Operating Days</label>
                  <div style={{ display: 'flex', gap: spacing.xs, flexWrap: 'wrap' }}>
                    {DAYS_OF_WEEK.map((day) => (
                      <button key={day} type="button" disabled={saving} onClick={() => toggleDay(day)} style={{
                        padding: `${spacing.xs} ${spacing.sm}`, borderRadius: borderRadius.md, border: 'none',
                        backgroundColor: form.operatingDays.includes(day) ? colors.primary.yellow : colors.neutral.gray800,
                        color: form.operatingDays.includes(day) ? colors.neutral.black : colors.neutral.gray400,
                        fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium,
                        cursor: saving ? 'not-allowed' : 'pointer', fontFamily: typography.fontFamily.body,
                      }}>{day}</button>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                  <LF label="Kod Kurikulum" style={{ flex: '1 1 140px' }}>
                    <input type="text" value={form.curriculumCode} onChange={(e) => setForm((f) => ({ ...f, curriculumCode: e.target.value }))}
                      disabled={saving} style={inputSt()} maxLength={30} />
                  </LF>
                  <LF label="Tarikh Pemeriksaan Terakhir" style={{ flex: '1 1 160px' }}>
                    <input type="date" value={form.lastInspectionDate} max={new Date().toISOString().slice(0, 10)}
                      onChange={(e) => setForm((f) => ({ ...f, lastInspectionDate: e.target.value }))}
                      disabled={saving} style={inputSt()} />
                  </LF>
                  <LF label="Status Pemeriksaan" style={{ flex: '1 1 160px' }}>
                    <input type="text" value={form.inspectionStatus} onChange={(e) => setForm((f) => ({ ...f, inspectionStatus: e.target.value }))}
                      placeholder="cth: PASSED" disabled={saving} style={inputSt()} maxLength={30} />
                  </LF>
                </div>
                <LF label="Nota / Notes">
                  <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    rows={3} disabled={saving} style={{ ...inputSt(), resize: 'vertical' }} />
                </LF>
              </div>
              {formError && <p style={{ margin: `${spacing.sm} 0 0`, color: '#f87171', fontSize: typography.fontSize.sm }}>{formError}</p>}
              <div style={{ display: 'flex', gap: spacing.sm, marginTop: spacing.md }}>
                <Button variant="primary" size="sm" onClick={() => void handleSave()} disabled={saving} loading={saving}>
                  {saving ? 'Menyimpan…' : 'Simpan / Save'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setShowForm(false); setFormError(null); }} disabled={saving}>Batal</Button>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 0, marginBottom: spacing.lg, backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, overflow: 'hidden' }}>
            {([['schools', '🕌 Sekolah'], ['stats', '📊 Statistik']] as const).map(([t, l]) => (
              <button key={t} onClick={() => setTab(t)} style={{
                flex: 1, padding: `${spacing.sm} ${spacing.xs}`, background: 'none',
                borderBottom: tab === t ? `2px solid ${colors.primary.yellow}` : '2px solid transparent',
                borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                color: tab === t ? colors.primary.yellow : colors.neutral.gray400,
                fontWeight: tab === t ? typography.fontWeight.semibold : typography.fontWeight.medium,
                fontSize: typography.fontSize.sm, cursor: 'pointer', fontFamily: typography.fontFamily.body,
              }}>{l}</button>
            ))}
          </div>

          {/* Schools tab */}
          {tab === 'schools' && (
            <>
              <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.lg, marginBottom: spacing.lg }}>
                <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div style={{ flex: '1 1 160px' }}>
                    <label style={labelSt}>Peringkat MORA</label>
                    <select value={filterLevel} onChange={(e) => setFilterLevel(e.target.value)} disabled={loading} style={inputSt()}>
                      <option value="">Semua</option>
                      {MORA_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </div>
                  <div style={{ flex: '1 1 160px' }}>
                    <label style={labelSt}>Daerah</label>
                    <select value={filterDistrict} onChange={(e) => setFilterDistrict(e.target.value)} disabled={loading} style={inputSt()}>
                      <option value="">Semua</option>
                      {DISTRICTS.map((d) => <option key={d} value={d}>{DIST_LABELS[d]}</option>)}
                    </select>
                  </div>
                  <Button variant="primary" size="sm" onClick={() => void load(1)} disabled={loading}>
                    {loading ? <Spinner size="sm" /> : 'Tapis / Filter'}
                  </Button>
                </div>
              </div>

              {error && <ErrorBanner msg={error} onRetry={() => void load(page)} />}
              {loading && schools.length === 0 && <LoadingBox />}
              {!loading && !error && schools.length === 0 && (
                <EmptyBox icon="🕌" title="Tiada Sekolah MORA" body="No MORA schools registered yet." />
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm, opacity: loading ? 0.7 : 1 }}>
                {schools.map((sc) => (
                  <div key={sc.id} style={{
                    backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl,
                    border: `1px solid ${colors.neutral.gray800}`, overflow: 'hidden', boxShadow: shadows.lg,
                  }}>
                    <div style={{ padding: spacing.lg }}>
                      <div style={{ display: 'flex', gap: spacing.xs, marginBottom: spacing.xs, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Pill label={sc.mora_level} color="#a78bfa" />
                        <Pill label={DIST_LABELS[sc.district] ?? sc.district} color="#60a5fa" />
                        {sc.inspection_status && <Pill label={sc.inspection_status} color="#4ade80" />}
                      </div>
                      <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>{sc.school_name}</p>
                      <p style={{ margin: `2px 0 0`, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                        {sc.school_code} · Kod MORA: {sc.mora_code}
                      </p>
                      <div style={{ display: 'flex', gap: spacing.lg, marginTop: spacing.xs, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>👨‍🏫 {sc.registered_ustaz} ustaz</span>
                        <span style={{ fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>👩‍🏫 {sc.registered_ustazah} ustazah</span>
                        <span style={{ fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>🎓 {sc.student_count} pelajar</span>
                        {sc.operating_hours && <span style={{ fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>🕐 {sc.operating_hours}</span>}
                      </div>
                      {sc.operating_days && sc.operating_days.length > 0 && (
                        <p style={{ margin: `2px 0 0`, fontSize: typography.fontSize.xs, color: colors.neutral.gray600 }}>
                          Hari: {sc.operating_days.join(', ')}
                        </p>
                      )}
                      {sc.last_inspection_date && (
                        <p style={{ margin: `2px 0 0`, fontSize: typography.fontSize.xs, color: colors.neutral.gray600 }}>
                          {E ? 'Last inspection:' : 'Pemeriksaan terakhir:'} {new Date(sc.last_inspection_date).toLocaleDateString(E ? 'en-GB' : 'ms-BN')}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: spacing.sm, marginTop: spacing.xl }}>
                  <Button variant="ghost" size="sm" disabled={page <= 1 || loading} onClick={() => void load(page - 1)}>← Sebelum</Button>
                  <span style={{ fontSize: typography.fontSize.sm, color: colors.neutral.gray400 }}>{page} / {totalPages}</span>
                  <Button variant="ghost" size="sm" disabled={page >= totalPages || loading} onClick={() => void load(page + 1)}>Seterusnya →</Button>
                </div>
              )}
            </>
          )}

          {/* Stats tab */}
          {tab === 'stats' && (
            <>
              {statsError && <ErrorBanner msg={statsError} onRetry={() => void loadStats()} />}
              {statsLoading && <LoadingBox />}
              {!statsLoading && !statsError && stats && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: spacing.sm }}>
                    {[
                      { label: 'Sekolah MORA',  value: stats.overview.total_schools,   color: '#a78bfa' },
                      { label: 'Ustaz',          value: stats.overview.total_ustaz,     color: '#60a5fa' },
                      { label: 'Ustazah',        value: stats.overview.total_ustazah,   color: '#f472b6' },
                      { label: 'Pelajar',        value: stats.overview.total_students,  color: '#4ade80' },
                    ].map((item) => (
                      <div key={item.label} style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.lg, textAlign: 'center', border: `1px solid ${item.color}22` }}>
                        <p style={{ margin: 0, fontSize: '28px', fontWeight: typography.fontWeight.bold, color: item.color }}>{item.value ?? '0'}</p>
                        <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>{item.label}</p>
                      </div>
                    ))}
                  </div>

                  {stats.byLevel.length > 0 && (
                    <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.lg }}>
                      <p style={{ margin: `0 0 ${spacing.md}`, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: colors.neutral.gray400 }}>MENGIKUT PERINGKAT</p>
                      <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                        {stats.byLevel.map((r) => (
                          <div key={r.mora_level} style={{ backgroundColor: 'rgba(167,139,250,0.1)', borderRadius: borderRadius.lg, padding: `${spacing.sm} ${spacing.lg}`, textAlign: 'center', minWidth: '120px' }}>
                            <p style={{ margin: 0, fontSize: '22px', fontWeight: typography.fontWeight.bold, color: '#a78bfa' }}>{r.count}</p>
                            <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.white }}>{r.mora_level}</p>
                            <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>{r.students} pelajar</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {stats.byDistrict.length > 0 && (
                    <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.lg }}>
                      <p style={{ margin: `0 0 ${spacing.md}`, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: colors.neutral.gray400 }}>MENGIKUT DAERAH</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                        {stats.byDistrict.map((r) => (
                          <div key={r.district} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: `${spacing.xs} ${spacing.sm}`, borderRadius: borderRadius.md, backgroundColor: 'rgba(255,255,255,0.03)' }}>
                            <span style={{ fontSize: typography.fontSize.sm, color: colors.neutral.white }}>{DIST_LABELS[r.district] ?? r.district}</span>
                            <span style={{ fontSize: typography.fontSize.sm, color: colors.neutral.gray400 }}>{r.mora_school_count} sekolah · {r.students} pelajar</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
    </>
  );
}

function LF({ label, children, style }: { label: string; children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={style}><label style={labelSt}>{label}</label>{children}</div>;
}
function Pill({ label, color }: { label: string; color: string }) {
  return <span style={{ display: 'inline-block', padding: `1px ${spacing.xs}`, backgroundColor: `${color}22`, color, borderRadius: borderRadius.full, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold }}>{label}</span>;
}
function ErrorBanner({ msg, onRetry }: { msg: string; onRetry: () => void }) {
  return (
    <div style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: borderRadius.lg, padding: spacing.lg, marginBottom: spacing.lg }}>
      <p style={{ margin: 0, color: '#f87171', fontSize: typography.fontSize.sm }}>{msg}</p>
      <Button variant="ghost" size="sm" onClick={onRetry} style={{ marginTop: spacing.xs }}>Cuba Lagi</Button>
    </div>
  );
}
function LoadingBox() { return <div style={{ display: 'flex', justifyContent: 'center', padding: spacing.xl }}><Spinner size="lg" /></div>; }
function EmptyBox({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, textAlign: 'center' }}>
      <div style={{ fontSize: '48px', marginBottom: spacing.sm }}>{icon}</div>
      <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>{title}</p>
      <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>{body}</p>
    </div>
  );
}
const labelSt: React.CSSProperties = { display: 'block', fontSize: typography.fontSize.xs, color: colors.neutral.gray400, marginBottom: spacing.xs };
function inputSt(): React.CSSProperties {
  return { width: '100%', backgroundColor: colors.neutral.black, border: `1px solid ${colors.neutral.gray800}`, borderRadius: borderRadius.md, color: colors.neutral.white, padding: `${spacing.xs} ${spacing.sm}`, fontSize: typography.fontSize.sm, outline: 'none', boxSizing: 'border-box', fontFamily: typography.fontFamily.body };
}
